import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks'
import {
  Building2, Check, Download, FileText, Globe, Import, Loader2, Pencil, Sparkles, Trash2, TriangleAlert, Undo2, Upload, UserRound,
} from 'lucide-preact'
import type {
  BrandAssetBackground,
  BrandAssetType,
  BrandRolesViewDto,
  DesignSystemImportLogoChoice,
  DesignSystemImportRoleChoice,
  BrandTokenRole,
  BrandTokenSetViewDto,
  KnowledgeBaseFileViewDto,
} from '@/types/knowledgeBase'
import { KnowledgeBaseCategory } from '@/types/knowledgeBase'
import { knowledgeBaseApi } from '@/api/knowledgeBase'
import { useAuth } from '@/auth/useAuth'
import { useCompany } from '@/hooks/useCompany'
import { useKnowledgeBaseSignalR, type KnowledgeBasePopulatedNotification } from '@/hooks/useKnowledgeBaseSignalR'
import { useToast } from '@/hooks/useToast'
import { FormActions } from '@/components/common/FormActions'
import { MarkdownView } from '@/components/common/MarkdownView'
import { RichTextBox } from '@/components/common/RichTextBox'
import { ConfirmationModal } from '@/components/modals/ConfirmationModal'
import { BRAND_CLASSES } from '@/constants/theme'
import { websiteLabel } from '@/utils/websiteLabel'
import { formatBytes } from '@/utils/fileSize'
import { routes } from '@/utils/routes'
import { holdsCompanyAdminRole } from '@/components/console/admin/useCompanyAdminAccess'
import {
  changedByLine,
  changedSinceConfirmed,
  chosenRoleValue,
  dayOf,
  editorWaitLabel,
  entryLabel,
  fileKind,
  fontRequests,
  openRoleEditor,
  rolePatch,
  tokenNoun,
  type RoleEditor,
  type TokenPatch,
} from '@/components/console/knowledgeBase/brandGuidelinesModel'
import { useBrandFonts } from '@/components/console/knowledgeBase/useBrandFonts'
import { RoleSwatches } from '../BrandGuidelinesRow'
import { BrandLogoRows } from './BrandLogoRows'
import { DesignSystemImportPanel } from './DesignSystemImportPanel'
import { BrandRoleTiles } from './BrandRoleTiles'
import { BrandTokenSections } from './BrandTokenSections'
import { useTokenEditor } from './brandEditing'
import {
  CONFIRM_BUTTON, H2, LEDE, QUIET_BUTTON, SECONDARY_BUTTON, SECONDARY_SMALL, SECTION, SQUARE_BADGE,
} from './brandGuidelinesStyles'
import { useBrandGuidelines, type BrandGuidelinesScope, type WriteOutcome } from './useBrandGuidelines'

interface Props {
  scope: BrandGuidelinesScope
  /**
   * Whether the person may change the brand the page shows: the company's own page, or a workspace's for those who can
   * change the workspace (ORB-1027). A campaign's page never does: campaigns take their workspace's brand.
   */
  canChange: boolean
}


/** The refusal the API keyed by this field, whatever case it used. */
function fieldError(outcome: WriteOutcome, field: string): string | null {
  if (outcome.ok) return null
  const key = Object.keys(outcome.fieldErrors).find(candidate => candidate.toLowerCase() === field.toLowerCase())
  return key ? outcome.fieldErrors[key] : null
}

/**
 * Brand Guidelines as the brand's design system, on its own page in the current app (ORB-904): logos, the roles
 * OrbitalX reads, every token, then the words and files.
 *
 * ⚑ Nothing is behind an edit mode. A role changes in its tile, the words in their section, a logo in its slot. The
 * colors are confirmed on their own and separately from the words, because asset QA trusts only a confirmed set while
 * emails and pages use a draft as it is.
 */
export function BrandGuidelinesView({ scope, canChange }: Props) {
  const { clientCompanyId, workspaceId, campaignId } = scope
  const level = campaignId ? 'Campaign' : workspaceId ? 'Workspace' : 'Company'
  const data = useBrandGuidelines(scope)
  const { showSuccess, showErrorUnless422, showInfo, showWarning, showError } = useToast()
  const { userProfile, userCompanyRoleSets } = useAuth()
  const { company, workspaces } = useCompany()
  const companyName = (company?.Id === clientCompanyId ? company?.Name : null)
    ?? workspaces.find(workspace => workspace.ClientCompanyId === clientCompanyId)?.ClientCompanyName
    ?? null
  const workspace = workspaceId ? workspaces.find(candidate => candidate.Id === workspaceId) ?? null : null
  const workspaceName = workspace?.Name ?? null

  const entry = data.load.kind === 'ready' ? data.load.entry : null
  const set = entry?.BrandTokens ?? null
  // Whose brand the page shows (ORB-1027): a workspace's own once it has one, else the company's it uses.
  const ownBrand = level === 'Workspace' && entry !== null && !entry.IsInherited
  const fromWorkspace = level === 'Campaign' && entry?.EffectiveLevel === 'Workspace'
  // Populate reads a workspace's own website for it, and is a company admin's at any level (ORB-817).
  const websiteToRead = level === 'Workspace' && workspace?.Website ? websiteLabel(workspace.Website) : null
  const canPopulate = canChange
    && (level === 'Company' || (websiteToRead !== null && holdsCompanyAdminRole(userCompanyRoleSets, clientCompanyId)))
  const [editor, setEditor] = useState<RoleEditor | null>(null)
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [words, setWords] = useState<string | null>(null)
  const [askingRemove, setAskingRemove] = useState<string | null>(null)
  const [askingPopulate, setAskingPopulate] = useState(false)
  const [askingRevert, setAskingRevert] = useState(false)
  const [importing, setImporting] = useState(false)
  const [populating, setPopulating] = useState(false)
  const [pending, setPending] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  // The slot the next chosen file fills: its type, and for a mark the background it is made for (ORB-968).
  const uploadAs = useRef<{ type?: BrandAssetType; background?: BrandAssetBackground | null }>({})
  const page = useRef<HTMLDivElement>(null)

  const fonts = useBrandFonts(useMemo(() => fontRequests(set), [set]))

  const companyRoles = useCompanyRoles(clientCompanyId, ownBrand)

  const reload = data.reload
  // ⚑ Only this page's own run: the company's on the company's page, this workspace's on a workspace's (ORB-1027).
  const isOwnRun = useCallback((notification: KnowledgeBasePopulatedNotification) =>
    notification.clientCompanyId === clientCompanyId && (notification.workspaceId ?? null) === (workspaceId ?? null),
  [clientCompanyId, workspaceId])
  const onPopulateCompleted = useCallback((notification: KnowledgeBasePopulatedNotification) => {
    if (!isOwnRun(notification)) return
    setPopulating(false)
    showSuccess('Brand Guidelines updated', `Webster read ${websiteToRead ?? 'the website'} again.`)
    void reload()
  }, [isOwnRun, websiteToRead, showSuccess, reload])
  const onPopulateFailed = useCallback((notification: KnowledgeBasePopulatedNotification) => {
    if (!isOwnRun(notification)) return
    setPopulating(false)
    if (notification.status === 'AlreadyRunning') showWarning('Already running', notification.errorMessage || 'Webster is already reading the website for this company.')
    else showError('Populate failed', notification.errorMessage || 'Webster could not read the website.')
  }, [isOwnRun, showWarning, showError])
  useKnowledgeBaseSignalR({ userId: canPopulate ? userProfile?.Id : undefined, onPopulateCompleted, onPopulateFailed })

  /** One write at a time. A refusal the API keyed by a field is the caller's to put beside that field. */
  const run = async (write: () => Promise<WriteOutcome>, done: [title: string, message: string], failed: string): Promise<WriteOutcome | null> => {
    if (pending) return null
    setPending(true)
    const outcome = await write()
    setPending(false)
    if (outcome.ok) showSuccess(...done)
    else showErrorUnless422(failed, { status: outcome.status, error: outcome.message })
    return outcome
  }

  const saveRole = async (value: string) => {
    if (editor === null) return
    const { role } = editor
    const outcome = await run(() => data.patchRoles(rolePatch(role, value)), ['Saved', value === '' ? `${role} removed.` : `${role} saved.`], `${role} not saved`)
    if (outcome === null) return
    if (outcome.ok) {
      setEditor(null)
      return
    }
    // A refusal stays beside the fields it is about; anything else was already said in a toast.
    const atField = fieldError(outcome, role) ?? (outcome.status === 422 ? outcome.message : null)
    setEditor(current => (current?.role === role ? { ...current, error: atField, removing: false } : current))
  }

  const saveEntry = (patch: TokenPatch) => {
    const { kind, dto } = patch
    const name = dto.Remove ? dto.Current : patch.kind === 'Family' ? patch.dto.Key : patch.dto.Name
    const label = entryLabel(kind, name?.trim() ?? '')
    const noun = tokenNoun(kind)
    return run(
      () => data.patchEntry(patch),
      dto.Remove ? ['Removed', `Removed ${label}.`] : ['Saved', `${dto.Current ? 'Saved' : 'Added'} ${label}.`],
      `${noun.charAt(0).toUpperCase()}${noun.slice(1)} not ${dto.Remove ? 'removed' : 'saved'}`)
  }

  // One entry editor for the whole page, so a form in one section closes nothing unseen in another and Confirm knows
  // what it waits on. While it or a role's editor is open, nothing else opens.
  const entryEdit = useTokenEditor(page, saveEntry)
  const locked = editor !== null || entryEdit.editor !== null

  const confirmColors = async () => {
    if (set === null) return
    setConfirmError(null)
    // ⚑ Sent back exactly as it arrived: through a Date it would lose the ticks the API compares to the millisecond.
    const outcome = await run(() => data.confirm({ ProvenanceAt: set.Provenance.At }), ['Confirmed', 'Colors confirmed.'], 'Colors not confirmed')
    if (outcome && !outcome.ok) setConfirmError(fieldError(outcome, 'BrandTokens') ?? (outcome.status === 422 ? outcome.message : null))
  }

  const saveWords = async (text: string, done: [title: string, message: string]) => {
    const outcome = await run(() => data.saveWords(text), done, 'Words not saved')
    if (outcome?.ok) setWords(null)
  }

  const chooseFile = (assetType?: BrandAssetType, background?: BrandAssetBackground | null) => {
    uploadAs.current = { type: assetType, background }
    if (!fileInput.current) return
    fileInput.current.accept = assetType ? 'image/*' : ''
    fileInput.current.click()
  }

  const markFile = async (file: KnowledgeBaseFileViewDto, background: BrandAssetBackground | null) => {
    const marked = background ? `for ${background.toLowerCase()} backgrounds` : 'for either background'
    await run(() => data.markFile(file.FileId, background), ['Saved', `${file.FileName} is now ${marked}.`], 'File not marked')
  }

  const removeFile = async (file: KnowledgeBaseFileViewDto) => {
    setAskingRemove(null)
    await run(() => data.deleteFile(file.FileId), ['Removed', `Removed ${file.FileName}.`], 'File not removed')
  }

  const startPopulate = async () => {
    setAskingPopulate(false)
    setPopulating(true)
    const outcome = await data.populate()
    if (!outcome.ok) {
      setPopulating(false)
      showErrorUnless422('Populate not started', { status: outcome.status, error: outcome.message })
      return
    }
    showInfo('Populate started', `Webster is reading ${websiteToRead ?? 'the website'}. This page updates when it finishes.`)
  }

  const importDesignSystem = (zip: File, logos: DesignSystemImportLogoChoice[], roles: DesignSystemImportRoleChoice[]) =>
    run(() => data.importDesignSystem(zip, logos, roles), ['Imported', `Imported ${zip.name}. Its colors count as confirmed.`], 'Design system not imported')

  const revertToCompanyBrand = async () => {
    setAskingRevert(false)
    await run(() => data.revertToCompanyBrand(),
      ['Removed', `${workspaceName ?? 'This workspace'} uses ${companyName ?? 'the company'}'s brand again.`], 'Brand not removed')
  }

  // ⚑ A single category's populate always overwrites, so it replaces a person's words as well as reviewed colors, and
  // either is asked first. An import's colors are never touched, and a Webster draft is Webster's own to replace. A
  // workspace using the company's brand loses nothing: its run writes the workspace's own.
  const replacesOwn = level === 'Company' || ownBrand
  const populateReplacesReviewedColors = replacesOwn && set !== null && set.Provenance.Source !== 'Import'
    && (set.Provenance.Confirmed || set.Provenance.Source === 'Person')
  const populateReplacesPersonsWords = replacesOwn && entry !== null
    && (entry.IsUserReviewed || (!entry.IsAiGenerated && (entry.TextContent ?? '').trim() !== ''))
  const populateLabel = websiteToRead ? `Populate from ${websiteToRead}` : 'Populate from website'
  const populateQuestion = [
    `Webster reads ${websiteToRead ?? 'the website'} again.`,
    populateReplacesPersonsWords ? 'Its new draft of the guidelines words replaces the ones a person wrote or reviewed.' : null,
    populateReplacesReviewedColors
      ? "It replaces Webster's colors, type and scales with a new draft, which needs confirming before asset QA uses it. Anything a person set stays as it is."
      : null,
  ].filter(Boolean).join(' ')

  if (data.load.kind === 'loading') return <BrandGuidelinesSkeleton />
  if (data.load.kind === 'failed') {
    return (
      <div role="alert" className="flex flex-col items-start gap-3 py-16">
        <p className="text-sm font-medium text-slate-900 dark:text-white">Brand Guidelines could not load.</p>
        <p className="text-sm text-slate-600 dark:text-slate-300">{data.load.message}</p>
        <button type="button" className={SECONDARY_BUTTON} onClick={() => void data.reload()}>Try again</button>
      </div>
    )
  }

  const files = entry?.Files ?? []
  const otherFiles = files.filter(file => !file.AssetType)
  const savedWords = entry?.TextContent ?? ''
  const wordsByWebster = entry !== null && entry.IsAiGenerated && !entry.IsUserReviewed && savedWords.trim() !== ''
  const emailsBlocked = !set?.Roles.Background.Hex || !set.Roles.Text.Hex
  const lightGround = set?.Roles.Background.Hex ?? '#FFFFFF'
  // Who Webster writes for from this page: the company on its own page, else the workspace.
  const writesFor = (level === 'Company' ? companyName : workspaceName) ?? (level === 'Company' ? 'this company' : 'this workspace')
  const change = set !== null ? changedByLine(set.Provenance) : null
  // Confirming is about the brand the page shows as its own: at a workspace using the company's it would only make a copy
  // of it, which stops the company's later changes reaching the workspace. The console offers neither there either.
  const confirmsOwn = level === 'Company' || ownBrand
  const canConfirm = canChange && confirmsOwn && set !== null && (!set.Provenance.Confirmed || change !== null)
  const waitingOn = editor !== null ? `the ${editor.role} change` : entryEdit.editor !== null ? editorWaitLabel(entryEdit.editor) : null

  return (
    <div ref={page} className="flex flex-col gap-7">
      <input ref={fileInput} type="file" className="hidden" onChange={event => {
        const file = event.currentTarget.files?.[0]
        const { type, background } = uploadAs.current
        event.currentTarget.value = ''
        if (file) void run(() => data.upload(file, type, background), ['Uploaded', `Uploaded ${file.name}.`], 'File not uploaded')
      }} />

      <header className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 lg:gap-8">
        <div className="min-w-0 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">Brand Guidelines</h1>
            <p className={LEDE}>Colors, type and logos Webster builds with, and the words that go with them.</p>
          </div>
          <div className="flex flex-col gap-3">
            <ul aria-label="About these guidelines" className="flex flex-wrap items-center gap-2">
              <li className={SQUARE_BADGE}>
                <Building2 className="w-3 h-3" aria-hidden="true" />
                {level === 'Company' ? 'Company-wide'
                  : ownBrand ? `${workspaceName ?? 'This workspace'}'s own`
                    : `From ${(fromWorkspace ? workspaceName : companyName) ?? (fromWorkspace ? 'its workspace' : 'the company')}`}
              </li>
              {set && <SourceBadge set={set} />}
              {set && <StateBadge set={set} />}
              {set && <ChangedBadge set={set} />}
              {emailsBlocked && (
                <li className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${BRAND_CLASSES.STATUS.WARNING}`}>
                  <TriangleAlert className="w-3 h-3" aria-hidden="true" />Needed before emails can be created
                </li>
              )}
            </ul>
            <ScopeNote
              level={level}
              canChange={canChange}
              ownBrand={ownBrand}
              fromWorkspace={fromWorkspace}
              companyName={companyName}
              workspaceName={workspaceName}
              companyRoles={companyRoles}
              companyHref={routes.settingsBrandGuidelines(clientCompanyId)}
              workspaceHref={workspaceId ? routes.scoped(clientCompanyId, workspaceId).brandGuidelines() : null}
              pending={pending}
              onRevert={() => setAskingRevert(true)}
            />
          </div>
        </div>
        {(canPopulate || canChange) && (
          <div className="flex-none flex flex-col items-start lg:items-end gap-2">
            <div className="flex flex-wrap items-center lg:justify-end gap-2">
              {canPopulate && (
                <button type="button" className={SECONDARY_BUTTON} disabled={populating || pending}
                  onClick={() => (populateReplacesReviewedColors || populateReplacesPersonsWords ? setAskingPopulate(true) : void startPopulate())}>
                  {populating ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Globe className="w-4 h-4" aria-hidden="true" />}
                  {populating ? 'Populating...' : populateLabel}
                </button>
              )}
              {canChange && (
                <button type="button" className={SECONDARY_BUTTON} disabled={pending} onClick={() => setImporting(true)}>
                  <Import className="w-4 h-4" aria-hidden="true" />Import design system
                </button>
              )}
              {/* The page's one primary action, while a person has not confirmed the colors or a change since (ORB-973). */}
              {canConfirm && (
                <button type="button" className={CONFIRM_BUTTON} disabled={pending || waitingOn !== null} onClick={() => void confirmColors()}
                  aria-describedby={waitingOn !== null ? 'bg-confirm-wait' : undefined}>
                  Confirm these colors
                </button>
              )}
            </div>
            {canConfirm && waitingOn !== null && <p id="bg-confirm-wait" className="text-xs text-slate-500 dark:text-slate-400">Save or cancel {waitingOn} first.</p>}
            {confirmError && <p role="alert" className={`max-w-[44ch] text-sm lg:text-right ${BRAND_CLASSES.INPUT.ERROR_TEXT}`}>{confirmError}</p>}
          </div>
        )}
      </header>

      <article aria-label="The brand" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <BrandLogoRows
          files={files}
          lightGround={lightGround}
          canChange={canChange}
          pending={pending}
          askingRemove={askingRemove}
          onAskRemove={setAskingRemove}
          onRemove={file => void removeFile(file)}
          onUpload={chooseFile}
          onMark={(file, background) => void markFile(file, background)}
        />

        <BrandRoleTiles
          set={set}
          canChange={canChange}
          pending={pending}
          editor={editor}
          fonts={fonts}
          entryWaiting={entryEdit.editor !== null ? editorWaitLabel(entryEdit.editor) : null}
          onEdit={(role: BrandTokenRole) => { setConfirmError(null); setEditor(openRoleEditor(set?.Roles ?? null, role)) }}
          onEditorChange={setEditor}
          onCancel={() => setEditor(null)}
          onSave={() => { if (editor) void saveRole(chosenRoleValue(editor.role, editor.token, editor.literal)) }}
          onRemove={() => void saveRole('')}
        />

        <BrandTokenSections set={set} fonts={fonts} canChange={canChange} pending={pending} edit={entryEdit} locked={locked} />

        <section aria-labelledby="bg-words-h" className={SECTION}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
            <div className="min-w-0 flex flex-col gap-1.5">
              <div className="flex flex-wrap items-center gap-3">
                <h2 id="bg-words-h" className={H2}>Guidelines</h2>
                {wordsByWebster && (
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${BRAND_CLASSES.STATUS.DRAFT}`}>
                    <Sparkles className="w-3 h-3" aria-hidden="true" />Webster draft, not reviewed
                  </span>
                )}
              </div>
              <p className={LEDE}>The brand book in words. Webster reads it before it writes anything for {writesFor}.</p>
            </div>
            {canChange && words === null && (
              <div className="flex-none flex items-center gap-2">
                <button type="button" className={SECONDARY_BUTTON} disabled={pending} onClick={() => setWords(savedWords)}>
                  <Pencil className="w-4 h-4" aria-hidden="true" />{savedWords.trim() ? 'Edit words' : 'Add words'}
                </button>
                {wordsByWebster && confirmsOwn && (
                  <button type="button" className={SECONDARY_BUTTON} disabled={pending}
                    onClick={() => void saveWords(savedWords, ['Saved', 'Words marked as reviewed.'])}>
                    <Check className="w-4 h-4" aria-hidden="true" />Confirm words
                  </button>
                )}
              </div>
            )}
          </div>
          {words === null ? (
            savedWords.trim()
              ? <MarkdownView className="max-w-[72ch] text-slate-900 dark:text-slate-100">{savedWords}</MarkdownView>
              : <p className="text-sm text-slate-500 dark:text-slate-400">No guidelines words yet.</p>
          ) : (
            <form className="flex flex-col gap-4" onSubmit={event => { event.preventDefault(); void saveWords(words, ['Saved', 'Words saved.']) }}>
              <RichTextBox name="bg-words" label="Guidelines" value={words} onChange={setWords} />
              <FormActions isSubmitting={pending} isSaveDisabled={pending || words === savedWords} onCancel={() => setWords(null)}
                saveText={pending ? 'Saving...' : 'Save words'} className="px-0 bg-transparent border-0" />
            </form>
          )}
        </section>

        {(otherFiles.length > 0 || canChange) && (
          <section aria-labelledby="bg-files-h" className={`${SECTION} border-t border-slate-200 dark:border-slate-700`}>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 sm:gap-6">
              <div className="min-w-0 flex flex-col gap-1.5">
                <h2 id="bg-files-h" className={H2}>Files</h2>
                <p className={LEDE}>Other files Webster reads for {writesFor}, such as the full brand book.</p>
              </div>
              {canChange && (
                <button type="button" className={`flex-none ${SECONDARY_BUTTON}`} disabled={pending} onClick={() => chooseFile()}>
                  <Upload className="w-4 h-4" aria-hidden="true" />Upload a file
                </button>
              )}
            </div>
            {otherFiles.length === 0 ? <p className="text-sm text-slate-500 dark:text-slate-400">No other files yet.</p> : (
              <ul aria-label="Other files" className="border-t border-slate-200 dark:border-slate-700">
                {otherFiles.map(file => (
                  <li key={file.FileId} className="flex items-center gap-3.5 py-3.5 border-b border-slate-200 dark:border-slate-700">
                    <span aria-hidden="true" className="w-9 h-9 flex-none grid place-items-center rounded-lg bg-slate-50 dark:bg-slate-900 ring-1 ring-inset ring-slate-200 dark:ring-slate-700 text-slate-500 dark:text-slate-400">
                      <FileText className="w-4 h-4" />
                    </span>
                    <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                      <a href={file.DownloadUrl ?? undefined} target="_blank" rel="noopener noreferrer"
                        className="text-sm font-medium text-primary-700 dark:text-primary-400 truncate hover:underline">{file.FileName}</a>
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {fileKind(file.ContentType, file.FileName)} · {formatBytes(file.FileSizeBytes)}{dayOf(file.UploadedAt) ? ` · added ${dayOf(file.UploadedAt)}` : ''}
                      </span>
                    </span>
                    {canChange && (askingRemove === file.FileId ? (
                      <span className="flex-none flex items-center gap-1">
                        <button type="button" className={`${QUIET_BUTTON} text-red-600 dark:text-red-400`} disabled={pending}
                          onClick={() => void removeFile(file)}>Delete file</button>
                        <button type="button" className={QUIET_BUTTON} onClick={() => setAskingRemove(null)}>Keep</button>
                      </span>
                    ) : (
                      <button type="button" aria-label={`Delete ${file.FileName}`} title="Delete" disabled={pending}
                        onClick={() => setAskingRemove(file.FileId)}
                        className="flex-none p-1.5 rounded-md text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50">
                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                      </button>
                    ))}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </article>

      <ConfirmationModal
        isOpen={askingPopulate}
        type="warning"
        title={`Populate from ${websiteToRead ?? 'the website'}?`}
        message={populateQuestion}
        confirmText="Populate"
        onConfirm={() => void startPopulate()}
        onCancel={() => setAskingPopulate(false)}
      />

      {importing && (
        <DesignSystemImportPanel
          ownerName={writesFor}
          currentFiles={level === 'Company' || ownBrand ? files : []}
          pending={pending}
          onPreview={data.previewImport}
          onImport={importDesignSystem}
          onClose={() => setImporting(false)}
        />
      )}

      <ConfirmationModal
        isOpen={askingRevert}
        type="danger"
        title={`Use ${companyName ?? 'the company'}'s brand instead?`}
        message={`This deletes ${workspaceName ?? 'this workspace'}'s own Brand Guidelines: its colors, type, logos, files and words. ${workspaceName ?? 'The workspace'} and its campaigns then use ${companyName ?? 'the company'}'s. Pages and emails already built keep what they were built with.`}
        confirmText={`Use ${companyName ?? 'the company'}'s instead`}
        loading={pending}
        onConfirm={() => void revertToCompanyBrand()}
        onCancel={() => setAskingRevert(false)}
      />
    </div>
  )
}

/** The company's role colors, beside a workspace's own brand so the page can show what it replaces (ORB-1027). */
function useCompanyRoles(clientCompanyId: string, wanted: boolean): BrandRolesViewDto | null | undefined {
  const [roles, setRoles] = useState<BrandRolesViewDto | null | undefined>(undefined)
  useEffect(() => {
    if (!wanted) return
    let current = true
    void knowledgeBaseApi.getCompanyEntry(clientCompanyId, KnowledgeBaseCategory.BrandGuidelines).then(response => {
      if (current) setRoles(response.data?.BrandTokens?.Roles ?? null)
    })
    return () => { current = false }
  }, [clientCompanyId, wanted])
  return roles
}

interface ScopeNoteProps {
  level: 'Company' | 'Workspace' | 'Campaign'
  canChange: boolean
  ownBrand: boolean
  fromWorkspace: boolean
  companyName: string | null
  workspaceName: string | null
  companyRoles: BrandRolesViewDto | null | undefined
  companyHref: string
  workspaceHref: string | null
  pending: boolean
  onRevert: () => void
}

const NOTE_LINK = 'font-medium text-primary-700 dark:text-primary-400 underline underline-offset-2 hover:text-slate-900 dark:hover:text-white'

/** What a change on this page reaches, and how a workspace's own brand gives way to the company's again (ORB-1027). */
function ScopeNote({ level, canChange, ownBrand, fromWorkspace, companyName, workspaceName, companyRoles, companyHref, workspaceHref, pending, onRevert }: ScopeNoteProps) {
  const company = companyName ?? 'the company'
  const workspace = workspaceName ?? 'this workspace'
  if (level === 'Company') {
    return (
      <p className="text-sm text-slate-500 dark:text-slate-400">
        A change here reaches every {companyName ? `${companyName} ` : ''}workspace and campaign without a brand of its own.
      </p>
    )
  }
  if (level === 'Campaign') {
    return (
      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-600 dark:text-slate-300">
        <span>{fromWorkspace ? `Set for ${workspace}.` : `Set for all of ${company}.`} It cannot be changed for one campaign.</span>
        {fromWorkspace && workspaceHref
          ? <a href={workspaceHref} className={NOTE_LINK}>Edit {workspace}'s</a>
          : <a href={companyHref} className={NOTE_LINK}>Edit company-wide</a>}
      </p>
    )
  }
  if (!ownBrand) {
    return (
      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 max-w-[72ch] text-sm text-slate-600 dark:text-slate-300">
        <span>
          {workspaceName ?? 'This workspace'} uses {company}'s brand.
          {canChange ? ` Changing anything here gives ${workspace} its own copy; ${company}'s stays as it is.` : ''}
        </span>
        {!canChange && <a href={companyHref} className={NOTE_LINK}>Edit company-wide</a>}
      </p>
    )
  }
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2.5">
      <p className="max-w-[60ch] text-sm text-slate-600 dark:text-slate-300">
        {canChange ? `A change here reaches ${workspace} and its campaigns only.` : `Set for ${workspace} and its campaigns.`}
        {' '}{company}'s brand stays as it is
        {companyRoles
          ? <span className="inline-block align-[-2px] ml-1.5"><RoleSwatches roles={companyRoles} owner={company} /></span>
          : null}
      </p>
      {canChange && (
        <button type="button" className={`flex-none ${SECONDARY_SMALL}`} disabled={pending} onClick={onRevert}>
          <Undo2 className="w-4 h-4" aria-hidden="true" />Use {company}'s instead
        </button>
      )}
    </div>
  )
}

function SourceBadge({ set }: { set: BrandTokenSetViewDto }) {
  const { Source, SourceName } = set.Provenance
  // The set's name and time are its last change's, which the Changed badge says once a confirmed set has changed.
  const changed = changedSinceConfirmed(set.Provenance)
  const byName = changed ? null : set.Provenance.ByName
  const at = changed ? null : dayOf(set.Provenance.At)
  if (Source === 'Import') {
    return (
      <li className={SQUARE_BADGE}>
        <Download className="w-3 h-3" aria-hidden="true" />
        {[`Imported from ${SourceName ?? 'a design system'}`, at, byName].filter(Boolean).join(' · ')}
      </li>
    )
  }
  if (Source === 'Person') {
    return (
      <li className={SQUARE_BADGE}>
        <UserRound className="w-3 h-3" aria-hidden="true" />
        {[`Set by ${byName ?? 'a person'}`, at].filter(Boolean).join(' · ')}
      </li>
    )
  }
  return (
    <li className={SQUARE_BADGE}>
      <Globe className="w-3 h-3" aria-hidden="true" />
      {['From the website', SourceName, at].filter(Boolean).join(' · ')}
    </li>
  )
}

function StateBadge({ set }: { set: BrandTokenSetViewDto }) {
  const { Confirmed, ConfirmedByName, ConfirmedAt, Source } = set.Provenance
  if (Confirmed) {
    const day = dayOf(ConfirmedAt)
    return (
      <li className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${BRAND_CLASSES.STATUS.SUCCESS}`}>
        <Check className="w-3 h-3" aria-hidden="true" />
        {['Colors confirmed', ConfirmedByName ? `by ${ConfirmedByName}` : null].filter(Boolean).join(' ')}{day ? ` · ${day}` : ''}
      </li>
    )
  }
  return (
    <li className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${Source === 'Website' ? BRAND_CLASSES.STATUS.DRAFT : BRAND_CLASSES.STATUS.DEFAULT}`}>
      {Source === 'Website' && <Sparkles className="w-3 h-3" aria-hidden="true" />}
      {Source === 'Website' ? 'Colors are a Webster draft, not confirmed' : 'Colors not confirmed'}
    </li>
  )
}

/**
 * Who changed the tokens after they were confirmed, beside the confirmation it is newer than (ORB-973). An edit keeps the
 * set confirmed, so emails and asset QA carry on with it; confirming again replaces the stamp and clears this.
 */
function ChangedBadge({ set }: { set: BrandTokenSetViewDto }) {
  const change = changedByLine(set.Provenance)
  if (change === null) return null
  // In the draft colors when Webster made the change, as everything else Webster wrote on this page is.
  return (
    <li className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${change.byWebster ? BRAND_CLASSES.STATUS.DRAFT : BRAND_CLASSES.STATUS.DEFAULT}`}>
      {change.byWebster ? <Sparkles className="w-3 h-3" aria-hidden="true" /> : <Pencil className="w-3 h-3" aria-hidden="true" />}
      {change.text}
    </li>
  )
}

/** The page's own footprint while it loads, so nothing jumps when the brand arrives. */
function BrandGuidelinesSkeleton() {
  const block = 'bg-shimmer-light animate-shimmer rounded-lg'
  return (
    <div aria-busy="true" className="flex flex-col gap-7">
      <span className="sr-only" role="status">Loading Brand Guidelines</span>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">Brand Guidelines</h1>
          <p className={LEDE}>Colors, type and logos Webster builds with, and the words that go with them.</p>
        </div>
        <div className="flex gap-2">
          <span className={`${block} h-6 w-28`} />
          <span className={`${block} h-6 w-56`} />
        </div>
      </div>
      <div aria-hidden="true" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-6 sm:px-8 py-12 flex flex-col gap-10">
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-5">
          {[0, 1, 2, 3, 4].map(index => <span key={index} className={`${block} h-28`} />)}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-7">
          {[0, 1, 2].map(index => <span key={index} className={`${block} h-24`} />)}
        </div>
      </div>
    </div>
  )
}
